export enum TransactionType {
  CASH_IN = 'CASH_IN',
  CASH_OUT = 'CASH_OUT',
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED',
}

// ─── Fee Estimate ────────────────────────────────────────────────────────────

export interface FeeEstimateResponse {
  type: 'cash-in' | 'cash-out';
  amount: number;           // monto ingresado
  feePercentage: number;    // porcentaje de comisión (alias de commissionRate)
  commissionRate: number;   // tasa vigente del agente en %
  rateSource: string;       // 'global' | 'custom'
  fee: number;              // comisión en XAF
  currency: string;
  note: string;             // descripción legible del cobro
  // cash-in: monto acreditado al cliente (= amount); el agente asume el fee
  customerReceives?: number;
  agentDebited?: number;    // total debitado del float del agente (amount + fee)
  // cash-out: total debitado al cliente (amount + fee)
  customerPays?: number;
  agentCredited?: number;   // lo que entra al float del agente (= amount)
}

// ─── Validate Code (Paso 3 del flujo) ───────────────────────────────────────

export interface CashInValidateResponse {
  requestId: string;
  code: string;
  amount: number;
  fee: number;             // comisión en XAF (se descuenta del float del agente)
  commissionRate: number;  // tasa del agente en % (e.g. 1.5)
  customerReceives: number; // monto acreditado al cliente (= amount)
  currency: string;
  expiresAt: string;
  customer: {
    name: string;
    phone: string;       // enmascarado: "699****12"
    walletNumber: string;
  };
  agentFloatAvailable: number;
  canProcess: boolean;
}

export interface CashOutValidateResponse {
  requestId: string;
  code: string;
  amount: number;
  fee: number;             // comisión en XAF
  commissionRate: number;  // tasa del agente en % (e.g. 1.5)
  customerPays: number;    // total debitado al cliente (amount + fee)
  currency: string;
  expiresAt: string;
  customer: {
    name: string;
    phone: string;       // enmascarado
    walletNumber: string;
  };
}

// ─── Confirm (Paso 4 del flujo) ──────────────────────────────────────────────

export interface CashConfirmRequest {
  code: string;
  amount: number;
  agentPin: string;    // contraseña del agente
}

export interface CashConfirmResponse {
  success: boolean;
  transactionId: string;
  reference: string;        // "CAIN..." o "CAOT..."
  amount: number;
  fee: number;
  commissionRate: number;   // tasa aplicada en %
  customerReceives?: number; // cash-in: monto acreditado al cliente
  customerPays?: number;     // cash-out: total debitado al cliente (amount + fee)
  currency: string;
  confirmedAt: string;
  customerName: string;
  newWalletBalance: number;
}

// ─── Transactions list ───────────────────────────────────────────────────────

export interface CashTransaction {
  id: string;
  type: TransactionType;
  amount: number;
  commissionAmount: number;
  commissionRate: number;
  status: TransactionStatus;
  reference: string;

  // Cliente
  customerName: string;
  customerPhone: string;

  // Fechas
  createdAt: string;
  completedAt?: string | null;
}

// ─── List response ────────────────────────────────────────────────────────────

export interface TransactionListMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export interface TransactionListSummary {
  cashInCount: number;
  cashInAmount: number;
  cashOutCount: number;
  cashOutAmount: number;
  totalCommissions: number;
}

export interface TransactionListResponse {
  data: CashTransaction[];
  meta: TransactionListMeta;
  summary: TransactionListSummary;
}

// ─── Reversal ────────────────────────────────────────────────────────────────

export interface ReversalRequest {
  id: string;
  transactionId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  requestedAt: string;
}

export interface ReversalResponse {
  message: string;
  reversalRequest: ReversalRequest;
}
